import { HiOutlineDownload, HiOutlineEye } from 'react-icons/hi';

import {
  Button,
  Dialog,
  DialogContent,
  DialogTrigger,
  formatDate,
  formatNumber,
  ImageViewer,
  omit,
  StatusField,
  Switch,
} from '@amfintech/react-admin-ui';
import { createHelpers, createResource, sentenceCase } from '@amfintech/refine-react-admin';
import { capitalCase } from 'change-case';

import {
  AccountType,
  FundCategories,
  OrderDocument,
  OrderDocumentStatus,
  Payment,
  PaymentMethod,
  Signatory,
  SignatoryStatus,
  TransactionStatus,
  TransactionType,
  type Transaction
} from '~/api';

import { formatOrderDocumentStatus, handleDownload } from './order-resource';
import { resourceNames } from './resource-names';

const { defineFields, defineCardSection, defineShowPage, defineRelatedResourceSection } =
  createHelpers<Transaction>({
    resourceName: resourceNames.transaction,
  });

export const formatTransactionStatus = (status: TransactionStatus) => {
  switch (status) {
    case TransactionStatus.Void:
      return 'warn';
    case TransactionStatus.Cancelled:
      return 'danger';
    case TransactionStatus.Approved:
      return 'success';
    case TransactionStatus.Submitted:
      return 'info';
    case TransactionStatus.Rejected:
      return 'danger';
    default:
      return 'pending';
  }
};

export const formatTransactionType = (type: TransactionType) => {
  switch (type) {
    case TransactionType.Buy:
      return 'success';
    case TransactionType.Sell:
      return 'danger';
    case TransactionType.Switch:
      return 'info';
    default:
      return 'pending';
  }
};

export const formatSignatoryStatus = (status: SignatoryStatus) => {
  switch (status) {
    case SignatoryStatus.Completed:
      return 'success';
    default:
      return 'pending';
  }
};

export const formatFundCategory = (category: FundCategories) => {
  switch (category) {
    case FundCategories.Equity:
      return 'Equity';
    case FundCategories.Etf:
      return 'Money Market';
    case FundCategories.FixedIncome:
      return 'Fixed Income';
    case FundCategories.MoneyMarket:
      return 'Balanced';
    case FundCategories.Prs:
      return 'PRS';
    case FundCategories.Commodities:
      return 'Commodities';
    default:
      return category;
  }
}

export const transactionResource = createResource({
  name: resourceNames.transaction,
  label: 'Transactions',
  fields: defineFields([
    'id',
    'referenceNo',
    'transactionDatetime',
    'transactionType',
    'accountMasterAccountNo',
    {
      account: [
        {
          profile: ['type', 'companyName', 'fullName', 'idNo', 'idType', 'email'],
        },
      ],
    },
    { fund: ['fundName', 'fundCode', 'currencyCode'] },
    { switchToFund: ['fundName', 'fundCode'] },
    'amount',
    'navPrice',
    'quantity',
    'transactionCharge',
    'transactionStatus',
    'redemptionExtent',
    'createdAt',
    'updatedAt',
    'errorMessage',
    'orderId',
    {
      order: [
        'referenceNo',
        {
          payments: [
            'id',
            'orderId',
            'referenceNo',
            'amount',
            'currency',
            'transactedAt',
            'paymentMethod',
            'status',
          ],
        },
        {
          signatories: ['signature', 'status', 'provider'],
        },
      ],
    },
  ]),
  defaultValues: {},
  list: {
    downloadConfig: {
      filenamePrefix: 'TransactionReport',
      additionalFields: defineFields([
        {
          fund: ['currencyCode'],
        },
        {
          switchToFund: ['fundCode', 'fundName'],
        },
        {
          order: ['referenceNo'],
        },
        'quantity'
      ]),
      transactionDatetime: (value: Date) => formatDate(value, { formatType: 'dateOnly' }),
      'fund.fundCode': (fundCode: string, item: Transaction) => {
        if (item.transactionType === TransactionType.Switch) {
          return `${fundCode} → ${item.switchToFund?.fundCode ?? 'N/A'}`;
        }
        return fundCode;
      },
      'fund.fundName': (fundName: string, item: Transaction) => {
        if (item.transactionType === TransactionType.Switch) {
          return `${fundName} → ${item.switchToFund?.fundName ?? 'N/A'}`;
        }
        return fundName;
      },
      amount: (value: any, item: Transaction) => {
        if (item.transactionType === TransactionType.Buy) {
          return `${item.fund.currencyCode} ${formatNumber(item.amount)}`;
        } else {
          return `${formatNumber(item.quantity)} units`;
        }
      },
    },
    filters: {
      collapsible: 'expand-by-default',
      permanent: [
        {
          field: 'transactionStatus',
          operator: 'in',
          value: Object.values(TransactionStatus).filter(
            (status) => status !== TransactionStatus.Pending
          ),
        },
      ],
    },
  },
  filterControls: {
    '.account.is.profile.is.type': {
      type: 'toggle-switch',
      config: {
        label: 'Account Type',
        required: true,
        toggleItems: Object.entries(AccountType).map(([key, value]) => ({
          label: key,
          value: value,
        })),
      },
    },
    referenceNo: {
      type: 'text',
      config: {
        label: 'Transaction Ref. No.',
      },
      operator: 'contains',
    },
    '.order.is.referenceNo': {
      type: 'text',
      config: {
        label: 'Order Ref. No.',
      },
      operator: 'contains',
    },
    transactionDatetime: {
      type: 'daterange',
      config: {
        label: 'Transaction Date',
      },
    },
    transactionStatus: {
      type: 'select',
      config: {
        label: 'Transaction Status',
        options: Object.entries(omit(TransactionStatus, [TransactionStatus.Submitted, TransactionStatus.Pending] )).map(([key, value]) => ({
          label: key,
          value: value,
        })),
      },
      operator: 'eq',
    },
    '.account.is.masterAccountNo': {
      type: 'text',
      config: {
        label: 'Master Account Number',
      },
      operator: 'contains',
    },
  },
  allowCreate: false,
  allowDownload: true,
  defaultSorter: [{ field: 'transactionDatetime', order: 'desc' }],
  columns: ({ LinkToDetails }) => [
    {
      id: 'referenceNo',
      header: 'Transaction Ref. No.',
      accessorKey: 'referenceNo',
      cell: (data) => {
        const refNumber = data.cell.getValue<string>();
        return (
          <LinkToDetails resourceId={data.row.original.id} className="font-semibold">
            {refNumber}
          </LinkToDetails>
        );
      },
    },
    {
      id: 'order.referenceNo',
      header: 'Order Ref. No.',
      accessorKey: 'order.referenceNo',
      cell: (data) => {
        const refNumber = data.cell.getValue<string>();
        return (
          <LinkToDetails resourceName={resourceNames.order} resourceId={data.row.original.orderId!} className="font-semibold">
            {refNumber}
          </LinkToDetails>
        );
      },
    },
    {
      id: 'transactionDatetime',
      header: 'Transaction Date',
      accessorKey: 'transactionDatetime',
      cell: (data) => {
        const datetime = data.cell.getValue<Date>();
        return <>{formatDate(datetime, { formatType: 'dateOnly' })}</>;
      },
    },
    {
      id: 'transactionType',
      header: 'Action',
      accessorKey: 'transactionType',
      cell: (data) => {
        const type = data.cell.getValue<TransactionType>();
        return <StatusField status={formatTransactionType(type)} label={type} hideDot />;
      },
    },
    {
      id: 'account.profile.companyName',
      header: 'Client/ Customer Name',
      accessorKey: 'account.profile.companyName',
      // cell: (data) => {
      //   const profile = data.cell.getValue();
      //   return profile?.type === AccountType.Corporate ? profile.companyName : profile.fullName;
      // },
    },
    {
      id: 'accountMasterAccountNo',
      header: 'Master Account Number',
      accessorKey: 'accountMasterAccountNo',
    },
    {
      id: 'fund.fundCode',
      header: 'Fund Code',
      accessorKey: 'fund.fundCode',
      cell: (data) => {
        const fundCode = data.cell.getValue<string>();
        if (data.row.original.transactionType === TransactionType.Switch) {
          return `${fundCode} → ${data.row.original.switchToFund?.fundCode ?? 'N/A'}`;
        }
        return fundCode;
      },
    },
    {
      id: 'fund.fundName',
      header: 'Fund Name',
      accessorKey: 'fund.fundName',
      cell: (data) => {
        const fundName = data.cell.getValue<string>();

        if (data.row.original.transactionType === TransactionType.Switch) {
          return `${fundName} → ${data.row.original.switchToFund?.fundName ?? 'N/A'}`;
        }
        return fundName;
      },
    },
    {
      id: 'amount',
      header: 'Amount/Unit',
      accessorKey: 'amount',
      cell: (data) => {
        if (data.row.original.transactionType === TransactionType.Buy) {
          const amount = data.cell.getValue<number>();
          return `${data.row.original.fund.currencyCode} ${formatNumber(amount)}`;
        } else {
          const quantity = data.row.original.quantity;
          return `${formatNumber(quantity)} units`;
        }
      },
    },
    {
      id: 'transactionStatus',
      header: 'Transaction Status',
      accessorKey: 'transactionStatus',
      cell: (data) => {
        const status = data.cell.getValue<TransactionStatus>();
        return <StatusField status={formatTransactionStatus(status)} label={status} />;
      },
    },
  ],
  allowEdit: false,
  allowDelete: false,
  show: defineShowPage({
    sections: [
      defineCardSection({
        title: 'Transaction Details',
        collapsible: 'expand-by-default',
        fields: [
          'referenceNo',
          'transactionDatetime',
          'transactionType',
          {
            order: [
              'id',
              {
                user: ['email', 'name', 'username'],
              },
              {
                bankAccount: [
                  {
                    bank: ['name']
                  },
                  'accountNo'
                ],
              },
            ],
          },
          {
            account: [
              // 'masterAccountNo',
              { profile: ['companyName', 'idNo', 'idType'] },
            ],
          },
          {
            account: ['masterAccountNo'],
          },
          {
            fund: [
              'fundName',
              {
                fundPolicy: ['wholeSaleFlag'],
              },
              'category',
            ],
          },
          { switchToFund: ['fundName', 'category'] },
          'amount',
          'navPrice',
          'quantity',
          'transactionStatus',
          'transactionCharge',
          'redemptionExtent',
          'updatedAt',
          'createdAt',
          'errorMessage',
        ],
        displays: {
          referenceNo: {
            label: 'Transaction Reference Number',
          },
          transactionDatetime: {
            label: 'Transaction Date',
            type: 'date',
            formatType: 'dateOnly',
          },
          transactionType: {
            label: 'Transaction Action',
          },
          'order.user.email': {
            label: 'Email',
          },
          'order.user.name': {
            label: 'Full Name',
          },
          'fund.fundName': {
            label: 'Fund Name',
          },
          'fund.category': {
            label: 'Fund Category',
            render: (data: Transaction) => {
              return formatFundCategory(data.fund.category);
            },
          },
          'switchToFund.fundName': {
            label: 'Fund Name(Switch To)',
            hide: (data: Transaction) => {
              return data.transactionType !== TransactionType.Switch;
            },
          },
          'switchToFund.category': {
            label: 'Fund Category(Switch To)',
            render: (data: Transaction) => {
              return data.switchToFund ? formatFundCategory(data.switchToFund.category) : 'N/A';
            },
            hide: (data: Transaction) => {
              return data.transactionType !== TransactionType.Switch;
            },
          },
          amount: {
            label: 'Indicative Amount',
            render: (data: Transaction) => formatNumber(data.amount),
          },
          navPrice: {
            label: 'NAV Price',
            render: (data: Transaction) =>
              formatNumber(data.navPrice, {
                minDecimal: 4,
                maxDecimal: 4,
              }),
          },
          quantity: {
            label: 'Unit',
            render: (data: Transaction) => formatNumber(data.quantity),
          },
          transactionCharge: {
            label: 'Transaction Charge (%)',
            render: (data: Transaction) => `${formatNumber(data.transactionCharge)}%`,
          },
          transactionStatus: {
            label: 'Transaction Status',
          },
          'order.bankAccount.bank.name': {
            label: 'Bank Name',
          },
          'order.bankAccount.accountNo': {
            label: 'Bank Account Number',
          },
          createdAt: {
            label: 'Created At',
            type: 'date',
            formatType: 'dateAndTime',
          },
          updatedAt: {
            label: 'Last Updated At',
            type: 'date',
            formatType: 'dateAndTime',
          },
          'order.user.username': {
            label: 'Updated By',
          },
          'account.profile.highNetWorth': {
            label: 'High Net Worth',
            render: (data: Transaction) => {
              return <Switch checked={data.account.profile?.highNetWorth ?? false} disabled />;
            },
          },
          'fund.fundPolicy.wholeSaleFlag': {
            label: 'Wholesale Flag',
            render: (data: Transaction) => {
              return data.fund.fundPolicy?.wholeSaleFlag ? 'Yes' : 'No';
            },
          },
          redemptionExtent: {
            hide: (data: Transaction) => {
              return !data.redemptionExtent;
            },
            label: 'Redemption Extent',
            render: (data: Transaction) => {
              return data.redemptionExtent ? sentenceCase(data.redemptionExtent) : 'N/A';
            },
          },
          errorMessage: {
            label: 'Error Message',
            render: (data: Transaction) => {
              return <span className="text-red-500">{data.errorMessage}</span>;
            },
          },
        },
      }),
      defineRelatedResourceSection<OrderDocument>({
        relatedResourceName: resourceNames.orderDocument,
        fields: ['orderId'],
        relatedResourceFields: [
          'id',
          'status',
          'referenceId',
          'documentType',
          'document',
          'orderId', 
          {
            order: ['referenceNo'] 
          },
        ],
        title: 'Documents',
        filterConfig: {
          collapsible: 'expand-by-default',
        },
        columns: () => [
          {
            id: 'referenceId',
            header: 'Reference ID',
            accessorKey: 'referenceId',
            cell: (data) => {
              const referenceId = data.cell.getValue<string>();
              return referenceId ? referenceId : 'No Reference ID';
            },
          },
          {
            id: 'documentType',
            header: 'Type',
            accessorKey: 'documentType',
            cell: (data) => {
              return capitalCase(data.cell.getValue<string>());
            },
          },
          {
            id: 'status',
            header: 'Status',
            accessorKey: 'status',
            cell: (data) => {
              const status = data.cell.getValue<OrderDocumentStatus>();
              return <StatusField status={formatOrderDocumentStatus(status)} label={status} />;
            },
          },
          {
            id: 'document',
            header: 'Document',
            accessorKey: 'document',
            cell: (data) => {
              const document = data.cell.getValue<string>();
              if (!document) return 'No Document';
              return (
                <Dialog>
                  <div className="flex gap-2">
                    <DialogTrigger asChild>
                      <Button variant={'solid'} size="sm" className="gap-2">
                        <HiOutlineEye className="h-4 w-4" />
                        View
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-h-[80vh] max-w-[60vw] w-[fit-content] overflow-y-auto">
                      <ImageViewer src={document} alt="Document" />
                  </DialogContent>
                  <Button
                      variant={'solid'}
                      size="sm"
                      className="gap-2"
                      onClick={() => handleDownload(document, data.row.original.order.referenceNo || 'document')}
                    >
                      <HiOutlineDownload className="h-4 w-4" />
                      Download
                    </Button>
                  </div>
                </Dialog>
              );
            },
          },
        ],
        getFilterConfig: ({ resource }) => ({
          permanent: [
            {
              field: 'orderId',
              operator: 'eq',
              value: resource?.orderId ?? '',
            },
          ],
        }),
      }),
      defineRelatedResourceSection<Payment>({
        relatedResourceName: resourceNames.payment,
        fields: ['orderId'],
        relatedResourceFields: [
          'orderId',
          'id',
          'referenceNo',
          'amount',
          'paymentMethod',
          'currency',
          'paymentProof',
        ],
        title: 'Payments',
        filterConfig: {
          collapsible: 'expand-by-default',
        },
        columns: () => [
          {
            id: 'referenceNo',
            header: 'Reference Number',
            accessorKey: 'referenceNo',
          },
          {
            id: 'currency',
            header: 'Currency',
            accessorKey: 'currency',
          },
          {
            id: 'amount',
            header: 'Amount',
            accessorKey: 'amount',
            cell: (data) => {
              const amount = data.cell.getValue<number>();
              return formatNumber(amount);
            },
          },
          {
            id: 'paymentMethod',
            header: 'Payment Method',
            accessorKey: 'paymentMethod',
            cell: (data) => {
              const paymentMethod = data.cell.getValue<PaymentMethod>();
              return sentenceCase(paymentMethod);
            },
          },
          {
            id: 'paymentProof',
            header: 'Payment Proof',
            accessorKey: 'paymentProof',
            cell: (data) => {
              const paymentProof = data.cell.getValue<string>();
              if (!paymentProof) return 'No payment proof';
              return (
                <Dialog>
                  <div className="flex gap-2">
                    <DialogTrigger asChild>
                      <Button variant={'solid'} size="sm" className="gap-2">
                        <HiOutlineEye className="h-4 w-4" />
                        View
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-h-[80vh] max-w-[60vw] w-[fit-content] overflow-y-auto">
                      <ImageViewer src={paymentProof} alt="Payment Proof" />
                    </DialogContent>
                    <Button
                      variant={'solid'}
                      size="sm"
                      className="gap-2"
                      onClick={() => handleDownload(paymentProof, data.row.original.referenceNo)}
                    >
                      <HiOutlineDownload className="h-4 w-4" />
                      Download
                    </Button>
                  </div>
                </Dialog>
              );
            },
          },
        ],
        getFilterConfig: ({ resource }) => ({
          permanent: [
            {
              field: 'orderId',
              operator: 'eq',
              value: resource?.orderId ?? '',
            },
          ],
        }),
      }),
      defineRelatedResourceSection<Signatory>({
        relatedResourceName: resourceNames.signatory,
        fields: ['orderId'],
        relatedResourceFields: [{ authorisedSignatory: ['email'] }, 'orderId', 'id', 'signature', 'status'],
        title: 'Signatories',
        filterConfig: {
          collapsible: 'expand-by-default',
        },
        columns: ({ LinkToDetails }) => [
          {
            id: 'authorisedSignatory.email',
            header: 'Authoriser Email',
            accessorKey: 'authorisedSignatory.email',
          },
          {
            id: 'status',
            header: 'Status',
            accessorKey: 'status',
            cell: (data) => {
              const status = data.cell.getValue<SignatoryStatus>();
              return <StatusField status={formatSignatoryStatus(status)} label={status} />;
            },
          },
          {
            id: 'signature',
            header: 'Signature',
            accessorKey: 'signature',
            cell: (data) => {
              const signature = data.cell.getValue<string>();
              if (!signature) return 'No signature';
              return (
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant={'solid'} size="sm" className="gap-2">
                      <HiOutlineEye className="h-4 w-4" />
                      View
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <div className="flex justify-center items-center h-full">
                      <img src={signature} alt="Signature" className="max-w-full max-h-full" />
                    </div>
                  </DialogContent>
                </Dialog>
              );
            },
          },
        ],
        getFilterConfig: ({ resource }) => ({
          permanent: [
            {
              field: 'orderId',
              operator: 'eq',
              value: resource?.orderId ?? '',
            },
          ],
        }),
      }),
    ],
  }),
});
